import { useMemo } from "react";

import { useQuery } from "@apollo/client";

import { EventsApiResponse } from "../../../types/events/api";
import { buildEventQueryVariables } from "../../../utils/buildEventQueryVariables";
import { handleApolloError } from "../../../utils/handleApolloError";
import { useCustomerIdGuard } from "../../../hooks/useCustomerIdGuard";
import { GET_RECENT_EVENTS } from "../../../../rest/queries";
import { AutocompleteOptionType } from "../../../models/autocomplete";

export const useZoneOptions = () => {
  const customerId = useCustomerIdGuard();

  const variables = useMemo(
    () =>
      buildEventQueryVariables({
        customerId,
        modelId: "GASLEAK",
        pageSize: 500,
      }),
    [customerId]
  );

  const { data, loading } = useQuery<{
    events: EventsApiResponse;
  }>(GET_RECENT_EVENTS, {
    variables,
    skip: !customerId,
    fetchPolicy: "cache-and-network",
    onError: handleApolloError,
  });

  const zoneOptions = useMemo((): AutocompleteOptionType[] => {
    const events = data?.events?.data ?? [];
    const uniqueZones = new Map<string, AutocompleteOptionType>();

    events.forEach(event => {
      const zoneId =
        (event?.newestRecording?.rawEventInfo as any)?.data?.zone_id ?? "";

      if (zoneId && !uniqueZones.has(zoneId)) {
        uniqueZones.set(zoneId, { title: zoneId, value: zoneId });
      }
    });

    return Array.from(uniqueZones.values()).sort((a, b) =>
      (a?.title ?? "").localeCompare(b?.title ?? "")
    );
  }, [data]);

  return {
    zoneOptions,
    loading,
  };
};
